import Divider from '@/components/common/Divider';
import { shouldUseNopBalance } from '@/helpers/environmentHelper';
import { useAppSelector } from '@/state/hooks';
import { selectCurrentAccount } from '@/state/reducers/authSlice';
import {
    CollapsedBalances,
    selectAggregatedBalance,
    selectBalanceStatus,
    selectNopBalance
} from '@/state/reducers/balanceSlice';
import { useCurrency } from '@/utils/hooks/useCurrency';
import { useCurrencySymbol } from '@/utils/hooks/useCurrencySymbol';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import useLanguage from '@/utils/hooks/useLanguage';
import usePreviousState from '@/utils/hooks/usePreviousState';
import { useDebouncedSize } from '@/utils/hooks/useSize';
import BigNumber from 'bignumber.js';
import cn from 'classnames';
import { Fragment, useMemo, useRef, useState } from 'react';
import { useNumberFormatter } from 'react-aria';
import Balance from './Balance';
import OldBalance from './OldBalance';

interface BalancesProps {}

export default function Balances(props: BalancesProps) {
    const lang = useLanguage();
    const status = useAppSelector(selectBalanceStatus);
    const nopBalance = useAppSelector(selectNopBalance);
    const celerBalance = useAppSelector(selectAggregatedBalance);
    const currentAccount = useAppSelector(selectCurrentAccount);

    const baseCcyConfig = useCurrency(nopBalance?.baseCurrency || '');
    const baseCcyDecimals = Math.max(baseCcyConfig.order_decimals - 2, 0);

    const balancesBodyRef = useRef<HTMLDivElement>(null);
    const size = useDebouncedSize(balancesBodyRef);

    const formatBaseQty = useNumberFormatter({
        minimumFractionDigits: baseCcyDecimals,
        maximumFractionDigits: baseCcyDecimals
    });

    const [newRecords, setNewRecords] = useState<CollapsedBalances[]>([]);

    const balances = useMemo(() => {
        return shouldUseNopBalance ? nopBalance?.balances || [] : celerBalance;
    }, [nopBalance, celerBalance]);

    const isSameSettlementDate = useMemo(() => {
        const flattenDates = balances.flatMap((balance) => balance.rows).map((row) => row.valueDate);
        const set = Array.from(new Set(flattenDates));
        return set.length === 1;
    }, [balances]);

    const nopBalanceAccountMatched = useMemo(() => {
        return currentAccount === nopBalance?.account;
    }, [currentAccount, nopBalance]);

    const balanceTotals = useMemo(() => {
        return balances?.reduce(
            (acc, curr) =>
                BigNumber(acc)
                    .plus((curr as CollapsedBalances).totalAmountInBaseCurrency)
                    .toNumber(),
            0
        );
    }, [balances]);

    const prevBalances = usePreviousState(balances);

    const symbol = useCurrencySymbol(nopBalance?.baseCurrency || '');

    useDidUpdate(() => {
        if (nopBalance && shouldUseNopBalance) {
            const records = nopBalance?.balances?.filter(
                (balance) => !prevBalances.find((prev) => prev.currency === balance.currency)
            );
            setNewRecords(records || []);
        }
    }, [nopBalance]);

    return (
        <div ref={balancesBodyRef} className="flex flex-col h-full text-brand-text">
            {status === 'OPEN' && nopBalanceAccountMatched && shouldUseNopBalance && (
                <div className="balances w-full flex flex-col relative self-stretch grow basis-full overflow-y-auto">
                    {balances.map((balance, index) => (
                        <Fragment key={index}>
                            <Balance
                                balance={balance}
                                nopBalance={nopBalance}
                                isNewlyAdded={!!newRecords.find((record) => record.currency === balance.currency)}
                                isSameSettlementDate={!!isSameSettlementDate}
                                parentSize={size?.width}
                            />
                            <Divider />
                        </Fragment>
                    ))}
                    {balanceTotals !== undefined && (
                        <div className="flex font-semibold p-1 py-2 text-sm px-2">
                            <div className="text-left flex-[1_1_0] whitespace-nowrap">
                                {lang.balances.total} ({nopBalance?.baseCurrency})
                            </div>
                            <div
                                className={cn(
                                    'flex flex-[2_1_0] relative items-center justify-center gap-2 text-right',
                                    {
                                        ['text-brand-text']: balanceTotals === 0,
                                        ['text-brand-primary']: balanceTotals > 0,
                                        ['text-brand-red']: balanceTotals < 0
                                    }
                                )}>
                                <span className="flex-[1_1_0]">
                                    {balanceTotals > 0 ? '' : '-'}
                                    {symbol}
                                    {formatBaseQty.format(balanceTotals).replace('-', '')}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {status === 'OPEN' && !shouldUseNopBalance && (
                <div className="w-full flex flex-col relative self-stretch grow basis-full overflow-y-auto px-3 py-1">
                    {balances.map((balance, index) => (
                        <OldBalance key={index} balance={balance} isNewlyAdded={false} />
                    ))}
                </div>
            )}
            {status === 'CLOSED' && (
                <div className="flex flex-col gap-2 p-2 justify-center items-center h-full text-sm text-center text-brand-neutral-400">
                    <div className="italic">{lang.balances.unableToFetch}</div>
                    <div className="italic">{lang.balances.pleaseReachOut}</div>
                </div>
            )}
        </div>
    );
}
